/**
 * Sign in by a six-digit code sent to the address the nursery invited.
 *
 * A code rather than a link (docs/15): customers often read email on a phone
 * and order on a PC, and some mail filters open links before a person does,
 * which uses a one-time link up. `shouldCreateUser: false` - sign-ups are off
 * on the project too - so an address the nursery never invited gets no code
 * and no account.
 *
 * The page does not say whether an address is known. "We could not send a
 * code" reads the same for a typo and for a stranger.
 */

import { type SupabaseClient } from '@supabase/supabase-js';
import { useState } from 'react';

export function SignIn({
  client,
}: {
  readonly client: SupabaseClient;
}): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | undefined>(undefined);
  const [code, setCode] = useState('');
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  async function send(): Promise<void> {
    const address = email.trim().toLowerCase();
    if (!address.includes('@')) {
      setProblem('Type the email address the nursery has for you.');
      return;
    }
    setBusy(true);
    setProblem(undefined);
    const { error } = await client.auth.signInWithOtp({
      email: address,
      options: { shouldCreateUser: false },
    });
    setBusy(false);
    if (error !== null) {
      setProblem(
        'We could not send a code to that address. Check it, or ask the nursery to ' +
          'give you access.',
      );
      return;
    }
    setSentTo(address);
    setCode('');
  }

  async function verify(): Promise<void> {
    if (sentTo === undefined) return;
    setBusy(true);
    setProblem(undefined);
    const { error } = await client.auth.verifyOtp({
      email: sentTo,
      token: code.trim(),
      type: 'email',
    });
    setBusy(false);
    if (error !== null) {
      setProblem('That code did not work. Check it, or send a new one.');
    }
  }

  return (
    <main className="shell shell--narrow">
      <h1>Trade orders</h1>
      <p className="lede">
        San Michell Farms&apos; availability for trade customers. Sign in with the email
        address the nursery invited.
      </p>

      {problem === undefined ? null : (
        <p className="notice notice--bad" role="alert">
          {problem}
        </p>
      )}

      {sentTo === undefined ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <label className="field">
            <span className="field__label">Email address</span>
            <input
              className="input"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </label>
          <button className="button button--primary" type="submit" disabled={busy}>
            Send me a code
          </button>
        </form>
      ) : (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void verify();
          }}
        >
          <p>
            We sent a six-digit code to <strong>{sentTo}</strong>. It can take a minute
            to arrive.
          </p>
          <label className="field">
            <span className="field__label">Code</span>
            <input
              className="input input--code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={10}
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, ''));
              }}
            />
          </label>
          <button
            className="button button--primary"
            type="submit"
            disabled={busy || code.trim().length < 6}
          >
            Sign in
          </button>
          <button
            className="button"
            type="button"
            disabled={busy}
            onClick={() => {
              void send();
            }}
          >
            Send a new code
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              setSentTo(undefined);
              setProblem(undefined);
            }}
          >
            Use another address
          </button>
        </form>
      )}
    </main>
  );
}
